import { CheckCircle, XCircle } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetRegistrationQuery as GetRegistration, RegistrationStatus } from '__generated__/graphql';

import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import GetRegistrationQuery from './GetRegistrationQuery';
import RegistrationInfo from './RegistrationInfo';
import SetRegistrationStatusMutation from './SetRegistrationStatusMutation';
import UI from '../../../../common/UI';
import useLazyQuery from '../../../../api/useLazyQuery';
import useMutation from '../../../../api/useMutation';

type Registration = GetRegistration['registration'];

export interface SetStatusFormProps {
  registration: Registration;
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const SetStatusForm = ({
  registration, onSuccess, onCancel, refetchQueries,
}: SetStatusFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      status: registration.status,
    },
  });

  const [
    setRegistrationStatus,
    { error, loading },
  ] = useMutation(
    SetRegistrationStatusMutation,
    {
      variables: {
        input: {
          id: registration.id,
          status: form.values.status,
        },
      },
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.status_set'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const icons = {
    [RegistrationStatus.confirmed]: () => (
      <UI.Icon sc={{ color: 'success', mb: 0, size: 1.125 }}>
        <CheckCircle />
      </UI.Icon>
    ),
    [RegistrationStatus.waitlist]: () => (
      <UI.Icon sc={{ color: 'gray.400', mb: 0, size: 1.125 }}>
        <UI.Icons.Hourglass />
      </UI.Icon>
    ),
    [RegistrationStatus.cancelled]: () => (
      <UI.Icon sc={{ color: 'error', mb: 0, size: 1.125 }}>
        <XCircle />
      </UI.Icon>
    ),
  };

  return (
    <UI.Form onSubmit={() => setRegistrationStatus()}>
      <UI.FormGrid>
        <UI.H4>
          {t('set_registration_status')}
        </UI.H4>

        <RegistrationInfo registration={registration} />

        <UI.ServerErrors error={error} />

        <UI.InputGroup>
          <UI.InputLabel>
            {t('new_status')}
          </UI.InputLabel>
          <UI.RadioPill sc={{ vertical: true }}>
            {registration.allowed_statuses.map((status) => (
              <UI.Radio
                {...form.radio('status', status)}
                sc={{ box: true, flex: { justifyContent: 'space-between', alignItems: 'center' } }}
                key={status}
              >
                <UI.Strong sc={{ fontWeight: 500 }}>
                  {t(`common:registration_statuses.${status}`)}
                </UI.Strong>
                {icons[status]()}
              </UI.Radio>
            ))}
          </UI.RadioPill>
        </UI.InputGroup>

        {registration.purchase.batch_size > 1 && (
          <UI.Info>
            {t('set_group_ticket_status_warning', { count: registration.purchase.batch_size })}
          </UI.Info>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('confirm')}
          onClose={onCancel}
          loading={loading}
          valid
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseSetStatusModalProps {
  registrationId?: string;
  refetchQueries?: DocumentNode[];
}

const useSetStatusModal = ({
  registrationId, refetchQueries,
}: UseSetStatusModalProps) => {
  const [fetch, { data }] = useLazyQuery(
    GetRegistrationQuery,
    {
      variables: {
        id: registrationId,
      },
    },
  );

  const registration = data?.registration;

  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal maxWidth={600} close={closeModal}>
        {!registration && (
          <UI.PageLoader />
        )}

        {registration && (
          <SetStatusForm
            registration={registration}
            refetchQueries={refetchQueries}
            onSuccess={closeModal}
            onCancel={closeModal}
          />
        )}
      </UI.Modal>
    ),
    [registration, refetchQueries],
  );

  const handleOpen = () => {
    fetch();
    openModal();
  };

  return [handleOpen, closeModal];
};

export default useSetStatusModal;
